import { Component, OnInit, AfterViewInit, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EventService } from 'src/app/core/service/event.service';
import { HitosProcesalesService } from './hitos-procesales.service';
import { PartesService } from '../partes/partes.service';
import { SortEvent } from 'src/app/shared/advanced-table/sortable.directive';
import { Column } from 'src/app/shared/advanced-table/advanced-table.component';
import { Hito } from './hitos-procesales.model';
import { Router } from '@angular/router';
import { hitoTranslations } from './translations-hitos-procesales';
import { formatDateStringWithTime, getCurrentLanguage } from '../core/helpers/utils';
import { formatDateString } from '../core/helpers/utils';
import { CasosService } from '../casos/casos.service';
import { AuthenticationService } from '../core/service/auth.service';

// Define el tipo ModelKeys fuera de la clase
type ModelKeys = keyof Hito;

@Component({
  selector: 'app-hitos-procesales',
  templateUrl: './hitos-procesales.component.html',
  styleUrls: ['./hitos-procesales.component.scss']
})
export class HitosProcesalesComponent implements OnInit {

  records: Hito[] = [];
  recordsFueraPlazo: Hito[] = [];
  columns: Column[] = [];
  pageSizeOptions: number[] = [10, 25, 50, 100];
  buttonTitle: string = '';
  buttonLink: string = '';
  loaded: boolean = false;
  error: string = '';
  allDatasearch: Hito[] = [];
  allDatasearchFueraPlazo: Hito[] = [];
  id_caso!: string | null;
  estadoExpediente: string | undefined;
  disableOptions: boolean = false;
  disableAnadirHito: boolean = true;


  nuevoHito: Hito = {
    id_expediente: '',
    fecha_fin: '',
    descripcion: '',
    fecha: '',
    hora: ''
  };

  showConfirmDeleteModal: boolean = false;
  hitoToDelete: Hito | null = null;

  constructor(
    private eventService: EventService,
    private router: Router,
    private elRef: ElementRef,
    private HitosProcesalesService: HitosProcesalesService,
    private partesService: PartesService, // Inyectar PartesService
    private route: ActivatedRoute,
    private casosService: CasosService,
    private authService: AuthenticationService
  ) { }

  ngOnInit(): void {

    // Inicializo el día y la hora
    this.nuevoHito['hora'] = '23:59';

    // El resto de la lógica permanece igual...

    this.id_caso = this.route.snapshot.paramMap.get('id');
    if (this.id_caso) {
      // Asignar el id del expediente
      this.nuevoHito.id_expediente = this.id_caso;

      // Obtener el estado del expediente y las partes desde el caso
      this.casosService.getCaso(this.id_caso).subscribe({
        next: (caso) => {
          this.estadoExpediente = caso.estado;
          const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
          this.disableOptions = estadosCerrados.includes(this.estadoExpediente ?? '');

          // Configuración de opciones según el nivel de acceso del usuario
          this.authService.currentUser().subscribe(user => {
            if (user?.nivel_acceso === 'ADMIN') {
              this.disableAnadirHito = false;
            }
            this.loaded = true;
          });
        },
        error: (error) => {
          console.error('Error obteniendo el estado del expediente:', error);
          this.error = error;
        }
      });

      // Cargar pagos
      this._fetchData();
    }
  }


  isCaseClosed(): boolean {
    const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
    return estadosCerrados.includes(this.estadoExpediente ?? '');
  }



  _fetchData(): void {
    if (this.id_caso) {
      this.HitosProcesalesService.getHitosExpediente(this.id_caso).subscribe(
        (resHitos) => {
          // Ordenar datos dentro de plazo en orden ascendente
          const hitosProcesales = resHitos.filter(hito => hito['tipo'] === 'HITO');

          this.records = hitosProcesales
            .filter(hito => parseFloat(hito.dias_restantes ?? '0') >= 0)
            .sort((a, b) => (a.fecha_fin ?? '').localeCompare(b.fecha_fin ?? ''));

          // Ordenar datos fuera de plazo en orden descendente
          this.recordsFueraPlazo = hitosProcesales
            .filter(hito => parseFloat(hito.dias_restantes ?? '0') < 0)
            .sort((a, b) => (b.fecha_fin ?? '').localeCompare(a.fecha_fin ?? ''));

          this.allDatasearch = [...this.records];
          this.allDatasearchFueraPlazo = [...this.recordsFueraPlazo];
          this.initTableCofig();
          this.loaded = true;
        },
        (error: string) => {
          this.error = error;
          this.loaded = false;
        }
      );
    }
  }



  onSubmit(f: any): void {

    // Solo realiza la operación si el formulario es válido
    if (f.valid && this.id_caso) {

      // Combina fecha y hora en un formato válido para el backend
      const fecha = this.nuevoHito['fecha']; // Extrae la fecha (YYYY-MM-DD)
      const hora = this.nuevoHito['hora'];   // Extrae la hora (HH:mm)
      const fechaCompleta = `${fecha}T${hora}:00Z`;
      const nuevoHitoConFecha = {
        ...this.nuevoHito,
        fecha_fin: fechaCompleta, // Combina fecha y hora
      };

      //console.log("nuevoHito", nuevoHito)
      this.HitosProcesalesService.newHito(nuevoHitoConFecha).subscribe(
        (newHito) => {
          f.resetForm(); // Reinicia el formulario
          this.nuevoHito['hora'] = '23:59';
          f.controls['hora'].setValue('23:59');
          this._fetchData();
        },
        (error: string) => {
          console.error('Error al añadir el hito:', nuevoHitoConFecha);
        }
      );
    }
  }

  initTableCofig(): void {
    let currentLanguage: string = getCurrentLanguage();
    const currentTranslations = hitoTranslations[currentLanguage].TABLE;
    this.columns = [
      {
        name: 'descripcion',
        label: currentTranslations.DESCRIPCION,
        formatter: (record: Hito) => record.descripcion,
        width: 445,
      },
      {
        name: 'fecha_fin',
        label: currentTranslations.FECHA_FIN,
        formatter: (record: Hito) => record.fecha_fin ? formatDateStringWithTime(record.fecha_fin, currentLanguage) : '',
        width: 50,
      },
      {
        name: 'dias_restantes',
        label: currentTranslations.DIAS_RESTANTES,
        formatter: (record: Hito) => record.dias_restantes,
        sort: true,
        width: 50,
      },
      {
        name: 'accionesHitosProcesales',
        label: currentTranslations.ACTIONS,
        formatter: (record: Hito) => null,
        width: 50,
        sort: false
      }
    ];
  }

  onSort(event: SortEvent): void {
    if (event.direction === '') {
      this.records = this.allDatasearch; // Restablece los registros originales si no hay dirección
    } else {
      this.records = [...this.records].sort((a, b) => {
        let res: number;

        if (event.column === 'dias_restantes') {
          const numA = parseFloat(a.dias_restantes ?? '0') || 0; // Convertir a número
          const numB = parseFloat(b.dias_restantes ?? '0') || 0;
          res = this.compare(numA, numB); // Comparación numérica
        } else {
          res = this.compare(a[event.column] as string | number, b[event.column] as string | number);
        }

        return event.direction === 'asc' ? res : -res; // Orden creciente
      });
    }
  }

  onSortFueraPlazo(event: SortEvent): void {
    if (event.direction === '') {
      this.recordsFueraPlazo = this.allDatasearchFueraPlazo; // Restablece los registros originales
    } else {
      this.recordsFueraPlazo = [...this.recordsFueraPlazo].sort((a, b) => {
        let res: number;

        if (event.column === 'dias_restantes') {
          const numA = parseFloat(a.dias_restantes ?? '0') || 0; // Convertir a número
          const numB = parseFloat(b.dias_restantes ?? '0') || 0;
          res = this.compare(numA, numB); // Comparación numérica
        } else {
          res = this.compare(a[event.column] as string | number, b[event.column] as string | number);
        }

        return event.direction === 'desc' ? res : -res; // Orden decreciente
      });
    }
  }


  compare(v1: string | number, v2: string | number): any {
    return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
  }

  normalizeText(text: string): string {
    return text.trim().toLocaleLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  matches(row: Hito, term: string): boolean {
    let currentLanguage: string = getCurrentLanguage();
    return this.normalizeText((row.descripcion) as string).includes(this.normalizeText(term))
      || this.normalizeText(row.fecha_fin ? formatDateString(row.fecha_fin, currentLanguage) : '').includes(this.normalizeText(term))

  }

  searchData(searchTerm: string): void {
    if (searchTerm === '') {
      this.records = this.allDatasearch;
    } else {
      this.records = this.allDatasearch.filter(record => this.matches(record, searchTerm));
    }
  }

  searchDataFueraPlazo(searchTerm: string): void {
    if (searchTerm === '') {
      this.recordsFueraPlazo = this.allDatasearchFueraPlazo;
    } else {
      this.recordsFueraPlazo = this.allDatasearchFueraPlazo.filter(recordFueraPlazo => this.matches(recordFueraPlazo, searchTerm));
    }
  }

  openDeleteModal(pago: Hito) {
    this.hitoToDelete = pago;
    this.showConfirmDeleteModal = true;
  }

  closeDeleteModal() {
    this.showConfirmDeleteModal = false;
    this.hitoToDelete = null;
  }

  confirmDelete() {
    if (this.hitoToDelete?.id) {
      this.HitosProcesalesService.deleteHito(this.hitoToDelete.id).subscribe(() => {
        this._fetchData(); // Actualiza los datos después de borrar
        this.closeDeleteModal();
      });
    }
  }

}
